import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { LoggerService, tryNormalizeHttpError, RoutingService, HttpErrorModel, Occ } from '@spartacus/core';
import { BehaviorSubject, combineLatest, Observable, of, shareReplay } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  find,
  map,
  take,
  tap,
  mergeMap,
  share,
  switchMap
} from 'rxjs/operators';

import { DpErrorService } from '@app/core/services/error-handling.service';
import { DpUrlService } from '@app/core/services/url.service';
import { CustomerPrice, CustomerPriceError, PRICE_ERROR_TYPE } from '@app/core/types/backend/customer-price.type';
import type { DpOccProduct } from '@app/core/types/backend/dp-product.interface';
import type { DownloadFileInfo } from '@app/core/types/download-file-info.type';
import type { ProductDownloadList } from '@app/core/types/product-download-list.type';
import { DpProductsApiService } from '@app-modules/product/shared/services/products-api.service';
import { DpMeasurementSystemService } from '@app-modules/site-context/facade/measurement-system.service';

@Injectable({ providedIn: 'root' })
export class DpProductsService {
  readonly #currentProduct$ = new BehaviorSubject<DpOccProduct | undefined>(undefined);
  readonly #priceResponse$ = new BehaviorSubject<CustomerPrice | CustomerPriceError>({} as CustomerPrice);
  readonly #totalCurrentPrice = new BehaviorSubject<string>('-');
  readonly #logger = inject(LoggerService);
  readonly #measurementSystemService = inject(DpMeasurementSystemService);
  readonly #productsApiService = inject(DpProductsApiService);

  constructor(
    private readonly routingService: RoutingService,
    private readonly dpUrlService: DpUrlService,
    private readonly dpErrorService: DpErrorService
  ) {}

  get currentProduct$(): Observable<DpOccProduct | undefined> {
    return this.#currentProduct$.asObservable();
  }

  get totalCurrentPrice$(): Observable<string> {
    return this.#totalCurrentPrice.asObservable();
  }

  get priceResponse$(): Observable<CustomerPrice | CustomerPriceError> {
    return this.#priceResponse$.asObservable();
  }

  setPriceResponse(value: CustomerPrice | CustomerPriceError): void {
    this.#priceResponse$.next(value);
  }

  initializeListener(): Observable<DpOccProduct | Error | undefined> {
    return combineLatest([
      this.getProductCode(),
      this.dpUrlService.urlParts$,
      this.#measurementSystemService.getActive()
    ]).pipe(
      distinctUntilChanged(
        ([previousCode, _p, previousMeasurementSystem], [nextCode, _n, nextMeasurementSystem]) =>
          previousCode === nextCode && previousMeasurementSystem === nextMeasurementSystem
      ),
      switchMap(([code, parts]) => {
        if (parts?.market?.length > 0 && !!code) {
          return this.getProduct();
        }

        return of();
      }),
      filter((product): product is DpOccProduct => !!product),
      tap((productDetails: DpOccProduct) => {
        this.#currentProduct$.next(productDetails);
      }),
      catchError((error) => of(this.dpErrorService.handle(error)))
    );
  }

  getProduct(): Observable<DpOccProduct> {
    return this.getProductCode().pipe(
      filter((productCode) => !!productCode),
      mergeMap((productCode) =>
        this.#productsApiService.getProductDetails(productCode ?? '').pipe(
          catchError((error: HttpErrorResponse) => of(this.dpErrorService.handle(error))),
          shareReplay(1)
        )
      )
    );
  }

  fetchPrice(code?: string): Observable<CustomerPrice | CustomerPriceError> {
    return code ? this.#sendPriceRequest(code) : this.fetchPriceForCurrentProduct();
  }

  fetchPriceForCurrentProduct(): Observable<CustomerPrice | CustomerPriceError> {
    return this.getProductCode().pipe(
      find((code) => !!code),
      switchMap((code = '') => this.#sendPriceRequest(code)),
      take(1)
    );
  }

  updateTotalPrice(price: number): void {
    this.#productsApiService
      .getFormattedProductPrice(price)
      .pipe(
        take(1),
        tap((res) => {
          this.#totalCurrentPrice.next(res?.formattedValue ?? '-');
        })
      )
      .subscribe();
  }

  getProductCode(): Observable<string | undefined> {
    return this.routingService.getRouterState().pipe(
      map((state) => state.state.params?.productCode),
      share(),
      distinctUntilChanged()
    );
  }

  listProductDownloads(): Observable<DownloadFileInfo[]> {
    return this.getProductCode().pipe(
      filter((productCode) => !!productCode),
      mergeMap((productCode) => this.#productsApiService.getProductDownloads(productCode ?? '')),
      filter((downloadList): downloadList is ProductDownloadList => !!downloadList),
      map<ProductDownloadList, DownloadFileInfo[]>((data) => data.downloads ?? [])
    );
  }

  listClassifications(): Observable<Occ.Classification[] | undefined> {
    return combineLatest([this.getProductCode(), this.#measurementSystemService.getActive()]).pipe(
      mergeMap(([productCode]) =>
        this.#productsApiService
          .getProductClassifications(productCode ?? '')
          .pipe(map((product): Occ.Classification[] | undefined => product.classifications))
      )
    );
  }

  getLatestValidPrice(): Observable<CustomerPrice | undefined> {
    return this.priceResponse$.pipe(
      map((priceResponse: CustomerPrice | CustomerPriceError) =>
        this.instanceOfDpCustomerPriceError(priceResponse) ? undefined : priceResponse
      )
    );
  }

  instanceOfDpCustomerPriceError(object: CustomerPrice | CustomerPriceError): boolean {
    return object && 'errorType' in object;
  }

  #sendPriceRequest(productCode: string): Observable<CustomerPrice | CustomerPriceError> {
    return this.#productsApiService.getProductPrice(productCode ?? '').pipe(
      catchError((error) => of(this.#getPriceError(productCode, error))),
      tap((price) => this.setPriceResponse({ productCode, ...price })),
      share()
    );
  }

  #getPriceError(productCode: string, error: HttpErrorModel): CustomerPriceError {
    const isNotAllowed = (tryNormalizeHttpError(error, this.#logger) as HttpErrorModel)?.details?.find(
      (err) => err.type === 'ProductNotAllowedError'
    );
    return {
      productCode,
      errorType: isNotAllowed ? PRICE_ERROR_TYPE.NOT_ALLOWED_ERROR : PRICE_ERROR_TYPE.GENERIC_ERROR,
      messageKey: isNotAllowed ? 'dpProductInfo.summary.notAllowed' : 'dpProductInfo.summary.error'
    };
  }
}
